import useApi from 'helpers/api';
import Cookies from 'js-cookie';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from 'services/Auth.service';
import { v4 as uuid } from 'uuid';
import { useAuthenticatedUser } from './useAuthenticatedUser';

// ============================================================================
// CONFIGURAÇÕES DE SEGURANÇA E CONTROLE
// ============================================================================

/** Tempo mínimo entre tentativas de login (ms) */
const LOGIN_COOLDOWN_MS = 2000;

/** Tempo máximo para processar um código de autorização (ms) */
const CODE_MAX_AGE_MS = 120000; // 2 minutos

/** Chaves dos cookies OAuth2 */
const COOKIE_KEYS = {
  STATE: 'oauth2_state',
  LOGIN_SESSION: 'oauth2_login_session',
  LOGIN_TIMESTAMP: 'oauth2_login_timestamp'
};

// ============================================================================
// ESTADO GLOBAL (persiste entre remounts do React)
// ============================================================================

/** Cache de códigos processados: Map<code, timestamp> */
const processedCodes = new Map();

/** Promise de processamento em andamento: Map<code, Promise> */
const processingPromises = new Map();

/** Timestamp da última tentativa de login */
let lastLoginAttempt = 0;

/** Flag para indicar se um login está em andamento */
let loginInProgress = false;

// ============================================================================
// FUNÇÕES AUXILIARES PARA COOKIES
// ============================================================================

/**
 * Obtém o valor de um cookie OAuth2
 * @param {string} name - Nome do cookie
 * @returns {string|undefined} Valor do cookie
 */
const getOAuthCookie = name => {
  return Cookies.get(name);
};

/**
 * Define um cookie OAuth2 (session cookie - expira quando o navegador fecha)
 * @param {string} name - Nome do cookie
 * @param {string} value - Valor do cookie
 */
const setOAuthCookie = (name, value) => {
  // Cookie de sessão (sem expires) - mais seguro para OAuth2
  Cookies.set(name, value, { path: '/', sameSite: 'Lax' });
};

/**
 * Remove um cookie OAuth2
 * @param {string} name - Nome do cookie
 */
const removeOAuthCookie = name => {
  Cookies.remove(name, { path: '/' });
};

/**
 * Limpa códigos antigos do cache (garbage collection)
 */
const cleanupProcessedCodes = () => {
  const now = Date.now();
  for (const [code, timestamp] of processedCodes.entries()) {
    if (now - timestamp > CODE_MAX_AGE_MS) {
      processedCodes.delete(code);
      processingPromises.delete(code);
    }
  }
};

/**
 * Limpa completamente o estado OAuth2 dos cookies
 */
const clearOAuthState = () => {
  Object.values(COOKIE_KEYS).forEach(key => {
    removeOAuthCookie(key);
  });
};

/**
 * Limpa todos os cookies de autenticação (para logout completo)
 */
const clearAllAuthCookies = () => {
  // Limpar cookies OAuth2
  clearOAuthState();

  // Limpar cookies de sessão e token
  Cookies.remove('token', { path: '/' });
  Cookies.remove('SESSION', { path: '/' });
};

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

/**
 * Hook customizado para gerenciar o fluxo OAuth2
 * @returns {Object} Objeto com funções e estados relacionados ao OAuth2
 */
export const useOAuth2 = () => {
  const navigate = useNavigate();
  const api = useApi();
  const { refreshUser } = useAuthenticatedUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false);

  /**
   * Inicia o fluxo de autenticação redirecionando para o servidor OAuth2
   */
  const initiateLogin = useCallback(() => {
    const now = Date.now();

    // Verificar cooldown entre tentativas
    if (now - lastLoginAttempt < LOGIN_COOLDOWN_MS) {
      console.warn('Login attempt too soon, please wait...');
      return;
    }

    // Verificar se já existe um login em andamento
    if (loginInProgress) {
      console.warn('Login already in progress...');
      return;
    }

    try {
      loginInProgress = true;
      lastLoginAttempt = now;

      // Limpar TUDO antes de iniciar novo login (estado residual causa invalid_grant)
      clearOAuthState();
      cleanupProcessedCodes();

      // Gerar identificadores únicos para esta sessão de login
      const state = uuid();
      const loginSession = uuid();

      // Armazenar estado nos cookies para validação no callback
      setOAuthCookie(COOKIE_KEYS.STATE, state);
      setOAuthCookie(COOKIE_KEYS.LOGIN_SESSION, loginSession);
      setOAuthCookie(COOKIE_KEYS.LOGIN_TIMESTAMP, now.toString());

      const authorizationUrl = AuthService.getAuthorizationUrl(state);

      // Redirecionar para o servidor OAuth2
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error('Error initiating login:', err);
      loginInProgress = false;
      setError(err.message || 'Failed to initiate login');
    }
  }, []);

  /**
   * Processa o callback OAuth2 após o redirecionamento
   * @param {URLSearchParams} searchParams - Parâmetros da URL
   * @returns {Promise<void>}
   */
  const handleAuthorizationCallback = useCallback(
    async searchParams => {
      const code = searchParams.get('code');
      const stateFromUrl = searchParams.get('state');

      // ========================================
      // VALIDAÇÕES INICIAIS (sem dependência de estado)
      // ========================================

      // 1. Verificar se há código
      if (!code) {
        const urlError = searchParams.get('error');
        const urlErrorDesc = searchParams.get('error_description');
        if (urlError) {
          setError(urlErrorDesc || urlError || 'Authorization failed');
        } else {
          setError('No authorization code provided');
        }
        return;
      }

      // 2. CRÍTICO: Verificar se este código já foi processado ou está sendo processado
      // Esta verificação usa um lock atômico para evitar condição de corrida
      if (processedCodes.has(code)) {
        console.warn(
          'OAuth2 code already processed:',
          code.substring(0, 10) + '...'
        );
        return;
      }

      // 3. Verificar se já existe um processamento em andamento para este código
      // Se existir, aguardar o resultado ao invés de iniciar novo processamento
      if (processingPromises.has(code)) {
        console.warn(
          'OAuth2 code already being processed, waiting...:',
          code.substring(0, 10) + '...'
        );
        try {
          await processingPromises.get(code);
        } catch {
          // Erro já tratado pela Promise original
        }
        return;
      }

      // ========================================
      // LOCK ATÔMICO: Marcar código como em processamento ANTES de qualquer validação
      // Isso previne a condição de corrida onde múltiplas execuções
      // passam pela verificação antes de qualquer uma registrar o código
      // ========================================
      
      // Criar uma Promise resolvível para coordenar múltiplas tentativas
      let resolveProcessing;
      let rejectProcessing;
      const processingPromise = new Promise((resolve, reject) => {
        resolveProcessing = resolve;
        rejectProcessing = reject;
      });
      
      // Registrar IMEDIATAMENTE que este código está sendo processado
      processingPromises.set(code, processingPromise);
      
      // Marcar ref local como inicializado
      hasInitialized.current = true;

      // Limpar URL imediatamente para evitar reuso em refresh
      window.history.replaceState({}, document.title, window.location.pathname);

      // ========================================
      // VALIDAÇÕES DE SEGURANÇA (após o lock)
      // ========================================

      // 4. Verificar se o código não expirou (baseado no timestamp do login)
      const storedTimestamp = getOAuthCookie(COOKIE_KEYS.LOGIN_TIMESTAMP);
      if (storedTimestamp) {
        const loginTime = parseInt(storedTimestamp, 10);
        const now = Date.now();
        if (now - loginTime > CODE_MAX_AGE_MS) {
          console.error('Authorization code expired');
          setError(
            'O código de autorização expirou. Por favor, tente novamente.'
          );
          clearOAuthState();
          processingPromises.delete(code);
          rejectProcessing(new Error('Code expired'));
          return;
        }
      }

      // 5. Verificar state (CSRF protection)
      const storedState = getOAuthCookie(COOKIE_KEYS.STATE);
      if (storedState && stateFromUrl !== storedState) {
        console.error(
          'State mismatch - possible CSRF attack or stale redirect'
        );
        setError(
          'Erro de segurança: state inválido. Por favor, tente novamente.'
        );
        clearOAuthState();
        processingPromises.delete(code);
        rejectProcessing(new Error('State mismatch'));
        return;
      }

      // ========================================
      // PROCESSAR CÓDIGO
      // ========================================

      // Registrar código como processado (agora é permanente)
      processedCodes.set(code, Date.now());
      loginInProgress = false;

      setIsLoading(true);
      setError(null);

      try {
        // Limpar estado OAuth2 dos cookies (código já foi validado)
        clearOAuthState();

        // Trocar código por token
        const tokenData = await AuthService.exchangeCodeForToken(code);
        AuthService.saveToken(tokenData.access_token);

        // Atualizar dados do usuário
        await refreshUser();

        // Verificar se precisa resetar senha
        const profile = await api.agents.myProfile();

        // Verificar se a resposta é um Response com erro
        if (profile instanceof Response) {
          if (!profile.ok) {
            throw new Error('Failed to get user profile');
          }
          // Se for 204 (No Content), não há perfil para verificar
          if (profile.status === 204) {
            navigate('/dashboards/tickets');
            resolveProcessing();
            return;
          }
        }

        // Verificar se precisa resetar senha
        if (profile?.passwordResetRequired) {
          navigate('/reset-password');
        } else {
          navigate('/dashboards/tickets');
        }
        
        resolveProcessing();
      } catch (err) {
        console.error('OAuth2 callback error:', err);
        setError(err.message || 'Authentication failed');
        rejectProcessing(err);
        // NÃO redirecionar automaticamente para evitar loop
      } finally {
        setIsLoading(false);
        // Limpar a Promise de processamento após um tempo
        // (manter por um tempo para evitar race conditions tardias)
        setTimeout(() => {
          processingPromises.delete(code);
        }, 5000);
      }
    },
    [navigate, refreshUser, api]
  );

  /**
   * Limpa o estado de erro
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reseta completamente o estado OAuth2 (útil para logout)
   * Limpa todos os cookies de autenticação e estado interno
   */
  const resetOAuthState = useCallback(() => {
    clearAllAuthCookies();
    cleanupProcessedCodes();
    // Limpar todas as Promises de processamento pendentes
    processingPromises.clear();
    processedCodes.clear();
    loginInProgress = false;
    hasInitialized.current = false;
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    initiateLogin,
    handleAuthorizationCallback,
    isLoading,
    error,
    clearError,
    resetOAuthState
  };
};
